// components/partials/Settings/WifiNetworkSettings.tsx
/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility, VisibilityOff } from '@material-ui/icons'
import { BORDER1_COLOR, PRIMARY_COLOR, TEXTFIELD_LABEL_COLOR, GRAY3_COLOR, getAppColors } from 'lib/constants'
import type { WifiNet, WifiNetType } from 'lib/types/wifi_nets'
import { getWifiNets, createWifiNet, updateWifiNet, deleteWifiNet } from 'models/api/wifi_nets'

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
})
const StyledTitleText = styled.div({
  color: PRIMARY_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.8rem',
  fontWeight: 'bold',
  lineHeight: '2.9rem',
  textAlign: 'left',
})
const StyledDescriptionText = styled.div({
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  lineHeight: '1.9rem',
  textAlign: 'left',
})
const StyledSeparator = styled.div({
  border: `1px solid ${BORDER1_COLOR}`,
  marginTop: '12px',
  marginBottom: '20px',
})
const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 0,
  paddingBottom: 4,
})
const ColTitle = styled.span({
  fontFamily: 'Montserrat',
  fontWeight: 'bold',
  fontSize: '1.4rem',
  letterSpacing: '0.1rem',
  textTransform: 'uppercase',
  color: PRIMARY_COLOR,
  flex: 1,
  lineHeight: '1.4rem',
})
const ColSub = styled.span({
  fontFamily: 'Montserrat',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  color: getAppColors('--color-text'),
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})
const ActionsCol = styled(ColSub)({
  justifyContent: 'flex-end',
  gap: 4,
  flex: 0.6,
})
const HeaderActionsSlot = styled.div({
  flex: 0.6,
  display: 'flex',
  justifyContent: 'flex-end',
})
const StyledButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '8px 20px',
  border: `2px solid ${PRIMARY_COLOR}`,
  fontFamily: 'Montserrat',
  boxShadow: '0px 4px 8px rgba(20, 48, 72, 0.1)',
  fontSize: '1.0rem',
  fontWeight: 'bold',
  letterSpacing: '0.1rem',
  marginRight: '5px',
  cursor: 'pointer',
  borderRadius: '2px',
})
const NameFieldBlock = styled.div({ width: '100%' })
const NameInputLabel = styled(InputLabel)({
  color: TEXTFIELD_LABEL_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginBottom: '20px',
})
const NameInput = styled(Input)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '5px 10px',
  fontFamily: 'Montserrat',
})
const TypeSelect = styled(Select)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '5px 10px',
  fontFamily: 'Montserrat',
  width: '100%',
})

const TYPE_LABEL: Record<WifiNetType, string> = {
  open: 'Open',
  personal: 'WPA Personal',
}

type Props = { orgId?: string }

export default function WifiNetworkSettings({ orgId }: Props) {
  const [rows, setRows] = useState<WifiNet[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  // form state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [type, setType] = useState<WifiNetType>('personal')
  const [ssid, setSsid] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const canLoad = useMemo(() => Boolean(orgId), [orgId])

  const load = async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const list = await getWifiNets(orgId)
      setRows(list)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!canLoad) return
    ;(async () => {
      await load()
    })()
  }, [canLoad, orgId]) // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null)
    setType('personal')
    setSsid('')
    setPassword('')
    setShowPassword(false)
    setModalOpen(true)
  }

  const openEdit = (row: WifiNet) => {
    setEditingId(row.id)
    setType(row.type ?? 'personal')
    setSsid(row.ssid)
    setPassword(row.password ?? '')
    setShowPassword(false)
    setModalOpen(true)
  }

  const onDelete = async (id: string) => {
    if (!orgId) return
    const ok = window.confirm('Delete this Wi-Fi network?')
    if (!ok) return
    setSaving(true)
    try {
      await deleteWifiNet(id, orgId)
      await load()
    } finally {
      setSaving(false)
    }
  }

  const trimmedSsid = ssid.trim()
  const isValid = !!trimmedSsid && (type === 'open' || (type === 'personal' && !!password))

  const onSave = async () => {
    if (!orgId || !isValid) return
    const fields = {
      ssid: trimmedSsid,
      type,
      password: type === 'open' ? null : password,
      user: null,
    }
    setSaving(true)
    try {
      if (editingId) {
        await updateWifiNet(editingId, orgId, fields)
      } else {
        await createWifiNet(orgId, fields)
      }
      await load()
      setModalOpen(false)
    } finally {
      setSaving(false)
    }
  }

  const onEnterKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!saving && isValid) onSave()
    }
  }

  return (
    <StyledContainer>
      <StyledTitleText>Wi-Fi Networks</StyledTitleText>
      <StyledDescriptionText>Create, edit, or delete Wi-Fi networks for this organization.</StyledDescriptionText>

      {/* Header */}
      <Row>
        <ColTitle>SSID</ColTitle>
        <ColTitle>Type</ColTitle>
        <HeaderActionsSlot>
          <StyledButton onClick={openCreate} disabled={!orgId} startIcon={<AddIcon />}>
            Add Wi-Fi
          </StyledButton>
        </HeaderActionsSlot>
      </Row>

      <StyledSeparator />

      {/* Body */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <span style={{ fontFamily: 'Montserrat', fontWeight: 'bold', fontSize: '1.2rem', color: '#29a096' }}>
          No Wi-Fi networks yet.
        </span>
      ) : (
        rows.map(row => (
          <Row key={row.id} style={{ marginBottom: 24 }}>
            <ColSub>{row.ssid}</ColSub>
            <ColSub>{row.type ? TYPE_LABEL[row.type] : '—'}</ColSub>
            <ActionsCol>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(row.id)} disabled={saving}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ActionsCol>
          </Row>
        ))
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit Wi-Fi Network' : 'Add Wi-Fi Network'}</DialogTitle>
        <DialogContent>
          <Box display="grid" gridGap={16} mt={1}>
            <NameFieldBlock>
              <NameInputLabel htmlFor="wifi-type">Type</NameInputLabel>
              <TypeSelect
                id="wifi-type"
                disableUnderline
                value={type}
                onChange={e => setType(e.target.value as WifiNetType)}
              >
                <MenuItem value="open">{TYPE_LABEL.open}</MenuItem>
                <MenuItem value="personal">{TYPE_LABEL.personal}</MenuItem>
              </TypeSelect>
            </NameFieldBlock>

            <NameFieldBlock>
              <NameInputLabel htmlFor="wifi-ssid">SSID</NameInputLabel>
              <NameInput
                id="wifi-ssid"
                disableUnderline
                fullWidth
                placeholder="Enter SSID"
                autoFocus
                value={ssid}
                onChange={e => setSsid(e.target.value)}
                onKeyDown={onEnterKey}
              />
            </NameFieldBlock>

            {type === 'personal' && (
              <NameFieldBlock>
                <NameInputLabel htmlFor="wifi-pass">Password</NameInputLabel>
                <NameInput
                  id="wifi-pass"
                  disableUnderline
                  fullWidth
                  placeholder="Enter password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(s => !s)}
                        onMouseDown={e => e.preventDefault()}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                  onKeyDown={onEnterKey}
                />
              </NameFieldBlock>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={() => setModalOpen(false)}>Cancel</StyledButton>
          <StyledButton onClick={onSave} disabled={saving || !isValid}>
            {saving ? 'Saving…' : 'Save'}
          </StyledButton>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  )
}
